import { matchSorter } from "match-sorter";
import { ReactNode, useDeferredValue, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useSessionStorage } from "usehooks-ts";
import { CopyButton } from "../components/CopyButton";
import { EmptyState } from "../components/EmptyState";
import { FancyLink } from "../components/FancyLink";
import { Flex } from "../components/Flex";
import { Monster } from "../components/Monster";
import { PageTitle } from "../components/PageTitle";
import { Paginator } from "../components/Paginator";
import { Search } from "../components/Search";
import { Select } from "../components/Select";
import { SelectDivider } from "../components/SelectDivider";
import { Spinner } from "../components/Spinner";
import { useAppContext } from "../hooks/useAppContext";
import { useSearch } from "../hooks/useSearch";
import { compare } from "../misc/compare";
import { Type, typesFromUserInput } from "../misc/data-types";
import { pickTranslation } from "../misc/pickTranslation";
import styles from "./ScreenPokedex.module.css";
import { useVersionGroup } from "../hooks/useVersionGroup";

export function ScreenPokedex(): ReactNode {
  const { allPokemon, isLoading } = useAppContext();
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const search = useSearch();
  const location = useLocation();
  const [query, setQuery] = useSessionStorage("pokedex.query", "");
  const [sortOrder, setSortOrder] = useSessionStorage("pokedex.sortOrder", "");
  const deferredQuery = useDeferredValue(query);
  const isStale = query !== deferredQuery;
  const [page, setPage] = useSessionStorage<number>("pokedex.page", 0);
  const navigate = useNavigate();
  const [versionGroup] = useVersionGroup();

  useEffect(() => {
    setPage(0);
  }, [versionGroup]);

  useEffect(() => {
    if (!location.search) {
      return;
    }
    if (search.has("q")) {
      setQuery(search.get("q") || "");
    }
    if (search.has("page")) {
      setPage(Number(search.get("page") || 1) - 1);
    }
    navigate({ ...location, search: "" }, { replace: true });
  }, [search, location]);

  const searchablePkmn = useMemo(() => {
    return allPokemon.map((p) => {
      return {
        ...p,
        speciesName: pickTranslation(p.speciesNames, language),
        speciesNameEnglish: pickTranslation(p.speciesNames, "en"),
        formName: pickTranslation(p.formNames, language),
        formNameEnglish: pickTranslation(p.formNames, "en"),
        total: p.hp + p.attack + p.defense + p.spAttack + p.spDefense + p.speed,
      };
    });
  }, [allPokemon, language]);

  const pkmn = useMemo(() => {
    const s = deferredQuery.trim().toLocaleLowerCase();
    const numberMatch = s.match(/^#?([0-9]+)$/);
    if (numberMatch) {
      const number = Number(numberMatch[1]);
      return searchablePkmn.filter((p) => p.number === number);
    }
    // The return value of `t` depends on the current value of `language`, but
    // the rules of hooks can't realize these. Pretend to use `language` here to
    // make it happy.
    const types = typesFromUserInput({ types: s, t, strict: true });
    let items = searchablePkmn.slice();
    if (types.length > 0) {
      items = searchablePkmn.filter((p) => {
        if (types.length === 1) {
          return p.types[0] === types[0] || p.types[1] === types[0];
        }
        if (types.length === 2 && types[1] === Type.none) {
          return p.types.length === 1 && p.types[0] === types[0];
        }
        return (
          p.types.slice().sort().join(" ") === types.slice().sort().join(" ")
        );
      });
    } else if (s) {
      items = matchSorter(searchablePkmn, s, {
        keys: [
          "speciesName",
          "speciesNameEnglish",
          "formName",
          "formNameEnglish",
          "number",
        ],
      });
    }
    if (sortOrder) {
      items = items.slice().sort((a, b) => {
        let n = 0;
        let m = 0;
        for (const stat of sortOrder.split("+")) {
          if (
            stat === "hp" ||
            stat === "attack" ||
            stat === "spAttack" ||
            stat === "defense" ||
            stat === "spDefense" ||
            stat === "speed"
          ) {
            n += a[stat];
            m += b[stat];
          }
        }
        return -compare(n, m);
      });
    }
    return items;
  }, [deferredQuery, searchablePkmn, language, t, sortOrder]);

  const permalink = new URL(window.location.href);
  {
    const newQuery = query.trim();
    if (newQuery) {
      permalink.searchParams.set("q", newQuery);
    }
    if (Number(page) > 0) {
      permalink.searchParams.set("page", String(page + 1));
    }
  }

  const updateSearch = (newQuery: string): void => {
    setQuery(newQuery);
    setPage(0);
  };

  return (
    <main className="center content-wide">
      <Flex direction="column" gap="xlarge" padding="large">
        <Flex direction="column" gap="large">
          <PageTitle title={t("navigation.pokedex")} />
          <div className={styles.searchArea}>
            <Search
              label={t("pokedex.search.description")}
              value={query}
              onChange={updateSearch}
            />
            <Select
              label={t("pokedex.sorting.sortBy")}
              value={sortOrder}
              onChange={(event) => {
                setSortOrder(event.target.value);
              }}
            >
              <option value="">{t("pokedex.sorting.auto")}</option>
              <SelectDivider />
              <option value="hp">{t("pokedex.stats.hp")}</option>
              <option value="attack">{t("pokedex.stats.attack")}</option>
              <option value="defense">{t("pokedex.stats.defense")}</option>
              <option value="spAttack">
                {t("pokedex.stats.specialAttack")}
              </option>
              <option value="spDefense">
                {t("pokedex.stats.specialDefense")}
              </option>
              <option value="speed">{t("pokedex.stats.speed")}</option>
              <option value="total">{t("pokedex.stats.total")}</option>
              <SelectDivider />
              <option value="attack+speed">
                {t("pokedex.stats.attack")}
                {" + "}
                {t("pokedex.stats.speed")}
              </option>
              <option value="spAttack+speed">
                {t("pokedex.stats.specialAttack")}
                {" + "}
                {t("pokedex.stats.speed")}
              </option>
              <option value="hp+defense+spDefense">
                {t("pokedex.stats.hp")}
                {" + "}
                {t("pokedex.stats.defense")}
                {" + "}
                {t("pokedex.stats.specialDefense")}
              </option>
              <option value="attack+defense">
                {t("pokedex.stats.attack")}
                {" + "}
                {t("pokedex.stats.defense")}
              </option>
              <option value="spAttack+spDefense">
                {t("pokedex.stats.specialAttack")}
                {" + "}
                {t("pokedex.stats.specialDefense")}
              </option>
            </Select>
          </div>
        </Flex>
        {isLoading ? (
          <Spinner />
        ) : (
          <Paginator
            currentPage={Number(page)}
            setPage={setPage}
            // Divide evenly between 1, 2, and 3 column layouts
            pageSize={12}
            emptyState={<EmptyState>{t("pokedex.search.notFound")}</EmptyState>}
            items={pkmn}
            renderPage={(page) => (
              <div className={styles.monsterGrid} data-stale={isStale}>
                {page.map((pokemon) => (
                  <Monster
                    key={pokemon.id}
                    pokemon={pokemon}
                    setQuery={updateSearch}
                  />
                ))}
              </div>
            )}
          />
        )}
        <Flex align="center">
          <CopyButton text={permalink.href}>{t("general.copyLink")}</CopyButton>
          <Flex flex="auto" />
          <FancyLink
            outlined
            to="/pokedex/help/"
            aria-label={t("pokedex.search.helpLong")}
          >
            {t("pokedex.search.help")}
          </FancyLink>
        </Flex>
      </Flex>
    </main>
  );
}
